import { useCallback, useEffect, useState } from 'react';

import { InboxPane } from './components/InboxPane';
import { LoginScreen } from './components/LoginScreen';
import { MessageListPane } from './components/MessageListPane';
import { ReaderPane } from './components/ReaderPane';
import { useInboxes } from './hooks/useInboxes';
import { useMessages } from './hooks/useMessages';
import { usePolling } from './hooks/usePolling';
import { useSession } from './hooks/useSession';
import { ApiError, type Attachment, type ClientConfig, type MessageDetail, api } from './lib/api';

/** How often the inbox list and the open inbox are refreshed. */
const POLL_INTERVAL_MS = 10_000;

/**
 * Which pane is showing on a narrow screen.
 *
 * On desktop all three are visible at once and this is ignored; below 900px
 * the stylesheet shows exactly one, so navigation becomes a step through them.
 */
type Step = 'inboxes' | 'messages' | 'reader';

export function App() {
  const session = useSession();
  const { token, signOut } = session;

  const [config, setConfig] = useState<ClientConfig | null>(null);
  const [step, setStep] = useState<Step>('inboxes');

  const inboxes = useInboxes(token, signOut);
  const [selectedAddress, setSelectedAddress] = useState<string | null>(null);
  const messages = useMessages(token, selectedAddress, signOut);

  const [selectedMessageId, setSelectedMessageId] = useState<string | null>(null);
  const [detail, setDetail] = useState<MessageDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState<string | null>(null);

  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState<string | null>(null);

  // Runtime configuration — the mail domain to show beside the name field, and
  // whether a relay is wired up. Fetched once per session.
  useEffect(() => {
    if (!token) {
      setConfig(null);
      return;
    }

    void api
      .config(token)
      .then(setConfig)
      .catch((cause: unknown) => {
        if (cause instanceof ApiError && cause.unauthorized) signOut();
      });
  }, [token, signOut]);

  // Land on the first address rather than an empty pane. Also recovers when
  // the selected address is deleted, in this tab or another.
  useEffect(() => {
    if (inboxes.inboxes.length === 0) {
      setSelectedAddress(null);
      return;
    }

    const stillExists = inboxes.inboxes.some((inbox) => inbox.address === selectedAddress);
    if (!stillExists) setSelectedAddress(inboxes.inboxes[0]?.address ?? null);
  }, [inboxes.inboxes, selectedAddress]);

  // Load the open message, and mark it read.
  useEffect(() => {
    if (!token || !selectedMessageId) {
      setDetail(null);
      setDetailError(null);
      return;
    }

    let cancelled = false;

    setDetailLoading(true);
    setDetailError(null);

    void api
      .getMessage(token, selectedMessageId)
      .then((loaded) => {
        // Guards against a slow response for a message the user has already
        // clicked away from overwriting the one they are now looking at.
        if (cancelled) return;

        setDetail(loaded);

        if (!loaded.seen) {
          messages.markSeenLocally(loaded.id);
          // Fire and forget: the badge has already updated, and a failed
          // mark-as-read corrects itself on the next poll.
          void api.markSeen(token, loaded.id).then(() => inboxes.refresh());
        }
      })
      .catch((cause: unknown) => {
        if (cancelled) return;
        if (cause instanceof ApiError && cause.unauthorized) signOut();
        else setDetailError('Could not load that message.');
      })
      .finally(() => {
        if (!cancelled) setDetailLoading(false);
      });

    return () => {
      cancelled = true;
    };
    // `messages` and `inboxes` are deliberately absent: including them would
    // re-run this effect on every poll and refetch the open message endlessly.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, selectedMessageId, signOut]);

  const refreshAll = useCallback((): void => {
    inboxes.refresh();
    messages.refresh();
  }, [inboxes, messages]);

  usePolling(refreshAll, POLL_INTERVAL_MS, token !== null);

  const selectInbox = (address: string): void => {
    setSelectedAddress(address);
    setSelectedMessageId(null);
    setDetail(null);
    setStep('messages');
  };

  const selectMessage = (id: string): void => {
    setSelectedMessageId(id);
    setSendError(null);
    setStep('reader');
  };

  const deleteInbox = (address: string): void => {
    void inboxes.remove(address);
    setSelectedMessageId(null);
    setDetail(null);
    setStep('inboxes');
  };

  const deleteMessage = (id: string): void => {
    if (!token) return;

    messages.removeLocally(id);
    setSelectedMessageId(null);
    setDetail(null);
    setStep('messages');

    void api
      .deleteMessage(token, id)
      .then(() => inboxes.refresh())
      .catch(() => messages.refresh());
  };

  const downloadAttachment = (attachment: Attachment): void => {
    if (!token || !detail) return;

    void api.downloadAttachment(token, detail.id, attachment).catch(() => {
      setDetailError('Could not download that attachment.');
    });
  };

  const reply = async (text: string): Promise<boolean> => {
    if (!token || !detail || !selectedAddress) return false;

    setSending(true);
    setSendError(null);

    try {
      await api.send(token, {
        from: selectedAddress,
        to: detail.peerAddress,
        subject: detail.subject.startsWith('Re:') ? detail.subject : `Re: ${detail.subject}`,
        text,
        ...(detail.rfcMessageId ? { inReplyTo: detail.rfcMessageId } : {}),
      });

      messages.refresh();
      inboxes.refresh();

      return true;
    } catch (cause) {
      if (cause instanceof ApiError && cause.unauthorized) signOut();
      else setSendError(cause instanceof ApiError ? cause.message : 'Could not send that reply.');

      return false;
    } finally {
      setSending(false);
    }
  };

  if (!token) {
    return (
      <LoginScreen
        signingIn={session.signingIn}
        error={session.error}
        onSubmit={(password) => void session.signIn(password)}
      />
    );
  }

  return (
    <div className="shell" data-step={step}>
      <InboxPane
        inboxes={inboxes.inboxes}
        selected={selectedAddress}
        loading={inboxes.loading}
        error={inboxes.error}
        mailDomain={config?.mailDomain ?? '…'}
        onSelect={selectInbox}
        onCreate={inboxes.create}
        onSignOut={signOut}
      />

      <MessageListPane
        address={selectedAddress}
        messages={messages.messages}
        selectedId={selectedMessageId}
        loading={messages.loading}
        error={messages.error}
        hasMore={messages.hasMore}
        loadingMore={messages.loadingMore}
        onSelect={selectMessage}
        onLoadMore={messages.loadMore}
        onDeleteInbox={deleteInbox}
        onBack={() => setStep('inboxes')}
      />

      <ReaderPane
        message={detail}
        loading={detailLoading}
        error={detailError}
        sendingEnabled={config?.sendingEnabled ?? false}
        sending={sending}
        sendError={sendError}
        inboxAddress={selectedAddress}
        onDelete={deleteMessage}
        onDownload={downloadAttachment}
        onReply={reply}
        onBack={() => setStep('messages')}
      />
    </div>
  );
}
